'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { PureComponent } from 'react';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { connect } from 'react-redux';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { findDOMNode } from 'react-dom';
import { push } from 'DesignData/actions/History';
import { CALYPSO_DARK, EERIE, OBSIDIAN } from 'HubStyleTokens/colors';
import Draggable from 'UIComponents/draggable/utils/Draggable';
import UIFolderNavItem from 'UIComponents/nav/UIFolderNavItem';
import UIIcon from 'UIComponents/icon/UIIcon';
import * as SelectionsActions from 'DesignData/actions/finder/Selections';
import { fetch as fetchUnifiedLayout } from 'DesignData/actions/UnifiedLayouts';
import * as FinderPropTypes from 'DesignData/propTypes/finder';
import * as appSelectors from 'DesignData/selectors/app';
import * as finderSelectors from 'DesignData/selectors/finder';
import { getParentIdOfRedirectedTab } from 'DesignData/selectors/tabs';
import { fileDragSource } from 'DesignFinderUI/components/dnd/FileDragDrop';
import FileTitle from 'DesignFinderUI/components/nav/FileTitle';
import withConnectedFile from 'DesignFinderUI/hocs/withConnectedFile';
import { hasShiftKey, hasCommandKey } from 'DesignData/enums/Keys';
import { GLOBAL_GROUP, LAYOUT } from 'DesignData/enums/FolderAssetTypes';
import { getIconNameFromFolderAssetType } from 'DesignData/lib/icon';
import { convertToAbsolute } from 'DesignData/lib/path';
const DraggableFolderNavItem = Draggable(UIFolderNavItem);
const {
  FilePropTypes
} = FinderPropTypes;

const makeMapStateToProps = () => {
  const getBulkPermissions = finderSelectors.makeGetAssetBulkPermissions();
  const getIsUpgradeableModule = finderSelectors.makeGetIsUpgradeableModule();
  return (state, props) => {
    const {
      fileId
    } = props;
    const isUpgradeableModule = getIsUpgradeableModule(state, props);
    let {
      showRenameFileConfirm
    } = finderSelectors.getSavedOptions(state);

    if (isUpgradeableModule) {
      showRenameFileConfirm = true;
    }

    const activeAssetId = appSelectors.getActiveAssetId(state);
    const revealedAssetId = finderSelectors.getRevealedAssetId(state);
    const redirectedParentTabId = getParentIdOfRedirectedTab(state, {
      tabId: fileId
    });
    return {
      bulkPermissions: getBulkPermissions(state, props),
      isActiveAsset: activeAssetId === fileId || !!activeAssetId && redirectedParentTabId === activeAssetId,
      isRevealedAsset: revealedAssetId === fileId,
      showRenameFileConfirm,
      isStaged: finderSelectors.getIsStagedFile(state, props)
    };
  };
};

const mapDispatchToProps = {
  deselectAsset: SelectionsActions.deselectAsset,
  selectFile: SelectionsActions.selectFile,
  shiftSelectAsset: SelectionsActions.shiftSelectAsset,
  pushRoute: push,
  revealInFinder: SelectionsActions.revealInFinder,
  fetchUnifiedLayout
};

class FileNode extends PureComponent {
  constructor(..._args) {
    super(..._args);

    this.scrollIntoView = () => {
      const fileNode = findDOMNode(this);

      if (fileNode) {
        this.scrollTimeout = setTimeout(() => {
          fileNode.scrollIntoView();
        }, 0);
      }
    };

    this.handleClick = evt => {
      evt.stopPropagation();

      if (hasCommandKey(evt) || hasShiftKey(evt)) {
        evt.preventDefault();
      }

      this.handleSelectFile(hasCommandKey(evt), hasShiftKey(evt));
    };

    this.handleOpenContextMenu = (...args) => {
      const {
        isSelected,
        file,
        fileType
      } = this.props;

      if (!isSelected) {
        this.handleSelectFile(false, false);
      }

      if ([GLOBAL_GROUP, LAYOUT].includes(fileType) && !file.generatedTemplatePath) {
        this.props.fetchUnifiedLayout(file.id);
      }

      this.props.openContextMenu(...args);
    };

    this.handleRenameCancel = () => {
      this.props.toggleRenameFile();
    };

    this.handleRenameConfirm = renameValue => {
      this.props.renameFile(renameValue);
      this.props.toggleRenameFile();
    };

    this.handleDeselectFile = () => {
      this.props.deselectAsset(this.props.fileId);
    };

    this.handleSelectFile = (isMultiSelect, shiftKey) => {
      const {
        fileId,
        fileType,
        parentFolderId,
        selectFile,
        shiftSelectAsset
      } = this.props;

      if (shiftKey) {
        shiftSelectAsset(fileId);
      } else {
        selectFile(fileId, fileType, parentFolderId, isMultiSelect);
      }
    };
  }

  componentDidMount() {
    this.props.connectDragPreview(getEmptyImage(), {
      captureDraggingState: true
    });

    if (this.props.isActiveAsset) {
      this.handleSelectFile();
    }

    if (this.props.isRevealedAsset) {
      this.scrollIntoView();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.isActiveAsset && this.props.isActiveAsset !== prevProps.isActiveAsset) {
      this.handleSelectFile();
    } else if (this.props.isRevealedAsset && this.props.isRevealedAsset !== prevProps.isRevealedAsset) {
      this.handleSelectFile();
    }

    if (!prevProps.isRevealedAsset && this.props.isRevealedAsset) {
      this.scrollIntoView();
    }

    if (prevProps.isRevealedAsset) this.props.revealInFinder(null);
  }

  componentWillUnmount() {
    clearTimeout(this.scrollTimeout);

    if (this.props.isSelected) {
      this.handleDeselectFile();
    }

    this.props.closeContextMenu();
  }

  renderTitle() {
    const {
      fileName,
      isInLockedFolder,
      isInSystemFolder,
      isReadOnly
    } = this.props;

    if (isInSystemFolder || isReadOnly) {
      return /*#__PURE__*/_jsx("span", {
        style: {
          color: OBSIDIAN
        },
        children: fileName
      });
    }

    if (isInLockedFolder) {
      return /*#__PURE__*/_jsx("i", {
        style: {
          color: EERIE
        },
        children: fileName
      });
    }

    return fileName;
  }

  renderIcon() {
    const {
      file,
      fileType,
      isInLockedFolder,
      isInSystemFolder,
      isReadOnly
    } = this.props;
    const fileIcon = getIconNameFromFolderAssetType(fileType, file.path);

    if (isInSystemFolder || isReadOnly) {
      return props => /*#__PURE__*/_jsx(UIIcon, Object.assign({}, props, {
        color: CALYPSO_DARK,
        name: fileIcon
      }));
    }

    if (isInLockedFolder) {
      return props => /*#__PURE__*/_jsx(UIIcon, Object.assign({}, props, {
        color: EERIE,
        name: fileIcon
      }));
    }

    return fileIcon;
  }

  render() {
    if (!this.props.file) {
      return null;
    }

    const {
      fileId,
      isActiveAsset,
      isDragging,
      isSelected,
      isStaged,
      showRenameFileConfirm,
      pushRoute,
      fileName,
      parentFolderName
    } = this.props;
    const itemClasses = classNames('file-item', isActiveAsset && 'is--active-asset', isDragging && 'is--dragging', isSelected && 'is--selected', isStaged && 'is--staged');
    return /*#__PURE__*/_jsx(DraggableFolderNavItem, Object.assign({}, this.props, {
      active: isSelected,
      "data-finder-node-name": fileName,
      "data-test-id": `${parentFolderName}:${fileName}`,
      "data-test-path": convertToAbsolute(this.props.file.path),
      iconLeft: this.renderIcon(),
      itemClassName: itemClasses,
      linkComponent: FileTitle,
      onClick: this.handleClick,
      openContextMenu: this.handleOpenContextMenu,
      onRenameCancel: this.handleRenameCancel,
      onRenameConfirm: this.handleRenameConfirm,
      showRenameConfirm: showRenameFileConfirm,
      title: this.renderTitle(),
      value: fileId,
      pushRoute: pushRoute
    }));
  }

}

FileNode.propTypes = {
  bulkPermissions: FinderPropTypes.bulkPermissions.isRequired,
  connectDragPreview: PropTypes.func.isRequired,
  deselectAsset: PropTypes.func.isRequired,
  file: FilePropTypes.file,
  fileId: FilePropTypes.fileId.isRequired,
  fileName: FilePropTypes.fileName.isRequired,
  fileTemplateType: FilePropTypes.fileTemplateType,
  fileType: FilePropTypes.fileType.isRequired,
  isActiveAsset: PropTypes.bool.isRequired,
  isDragging: PropTypes.bool.isRequired,
  isInLockedFolder: PropTypes.bool,
  isInSystemFolder: PropTypes.bool,
  isMultiSelected: PropTypes.bool.isRequired,
  isReadOnly: PropTypes.bool,
  isRevealedAsset: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
  isStaged: PropTypes.bool,
  isRenamingFile: PropTypes.bool.isRequired,
  closeContextMenu: PropTypes.func.isRequired,
  openContextMenu: PropTypes.func.isRequired,
  parentFolderId: FinderPropTypes.FolderPropTypes.folderId,
  parentFolderName: FinderPropTypes.FolderPropTypes.folderName,
  renameFile: PropTypes.func.isRequired,
  selectFile: PropTypes.func.isRequired,
  shiftSelectAsset: PropTypes.func.isRequired,
  showRenameFileConfirm: PropTypes.bool,
  toggleRenameFile: PropTypes.func.isRequired,
  pushRoute: PropTypes.func.isRequired,
  canDragIntoLayout: PropTypes.bool.isRequired,
  catalogWidget: PropTypes.object,
  revealInFinder: PropTypes.func.isRequired,
  fetchUnifiedLayout: PropTypes.func.isRequired
};
FileNode.defaultProps = {
  fileTemplateType: null,
  isInLockedFolder: false,
  showRenameFileConfirm: true
};
export default compose(withConnectedFile, connect(makeMapStateToProps, mapDispatchToProps), fileDragSource)(FileNode);